/**
 * Firecrawl Service
 * REAL web scraping - no hallucinations, no fake data
 * Production-ready for customer use
 */

import FirecrawlApp from '@mendable/firecrawl-js';
import { auth } from '../config/firebase';

const FIRECRAWL_API_KEY = import.meta.env.VITE_FIRECRAWL_API_KEY || '';

let firecrawl: FirecrawlApp | null = null;

function getClient(): FirecrawlApp {
  if (!firecrawl && FIRECRAWL_API_KEY) {
    firecrawl = new FirecrawlApp({ apiKey: FIRECRAWL_API_KEY });
  }
  if (!firecrawl) {
    throw new Error('Firecrawl API key not configured');
  }
  return firecrawl;
}

export interface ScrapedWebsiteData {
  markdown: string;
  html: string;
  title: string;
  description: string;
  language: string;
  images: string[];  // REAL image URLs from website!
  links: string[];
  metadata: any;
  screenshot?: string;
}

export interface RealConversion {
  type: 'contact' | 'purchase' | 'signup' | 'booking' | 'download';
  formId?: string;
  hasEmail: boolean;
  hasPhone: boolean;
  hasName: boolean;
  hasMessage: boolean;
  buttonText?: string;
  evidence: string;  // REAL evidence from HTML
}

export interface TonalityAnalysis {
  formality: 'formal' | 'informal';
  addressForm: 'Sie' | 'Du' | 'mixed' | 'none';
  confidence: number;
  evidence: string;
  recommendation: string;
  sieCount: number;
  duCount: number;
}

export const firecrawlService = {
  /**
   * Scrape website via Firebase Cloud Function (Server-Side)
   * Avoids CORS issues by using server-side proxy
   */
  async scrapeWebsite(url: string, options: { minAge?: number; maxAge?: number } = {}): Promise<ScrapedWebsiteData> {
    try {
      console.log('🔥 Calling Firebase Function for Firecrawl (Server-Side)...');
      
      // Get Firebase auth token
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        throw new Error('Sie müssen eingeloggt sein, um Websites zu analysieren.');
      }
      
      const idToken = await currentUser.getIdToken();
      
      // Call Firebase Cloud Function directly (onRequest with manual auth)
      const functionUrl = 'https://us-central1-aideline-pro.cloudfunctions.net/firecrawlProxyV2';
      
      const callScrapeProxy = async (ignoreCache: boolean) => {
        const response = await this.fetchWithRetry(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({
            data: {
              url,
              options: {
                minAge: options.minAge ?? 0,
                maxAge: options.maxAge ?? 172800000,
                ignoreCache
              }
            }
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        const responseData = await response.json();
        return responseData.result?.data || responseData.data;
      };

      let result = await callScrapeProxy(false);
      const hasUsableContent = Boolean(result?.html?.trim() || result?.markdown?.trim());

      if (!hasUsableContent) {
        console.warn('⚠️ Firecrawl returned empty content. Retrying once with ignoreCache=true...');
        result = await callScrapeProxy(true);
      }

      if (!result?.html?.trim() && !result?.markdown?.trim()) {
        throw new Error('Keine auswertbaren Inhalte gefunden (leere Crawl-Antwort).');
      }
      
      console.log(`✅ Firecrawl v2 scraped successfully`);
      console.log(`📄 Markdown length: ${result.markdown?.length || 0} chars`);
      console.log(`📄 HTML length: ${result.html?.length || 0} chars`);
      console.log(`🔗 Links found: ${result.links?.length || 0}`);
      
      // Extract ALL <img> tags from REAL HTML!
      const extractedImages: string[] = [];
      
      // Method 1: Parse ALL <img src="..."> from HTML
      if (result.html) {
        const imgRegex = /<img[^>]+src=["']([^"']+)["']/gi;
        const imgMatches = [...result.html.matchAll(imgRegex)];
        
        imgMatches.forEach(match => {
          let imgUrl = match[1];
          
          // Skip data: URLs, icons, small images
          if (imgUrl.startsWith('data:')) return;
          if (imgUrl.includes('icon') || imgUrl.includes('favicon')) return;
          if (imgUrl.includes('.svg') && imgUrl.length < 100) return;
          
          // Make absolute URLs
          if (imgUrl.startsWith('//')) {
            imgUrl = `https:${imgUrl}`;
          } else if (imgUrl.startsWith('/')) {
            const baseUrl = new URL(url);
            imgUrl = `${baseUrl.protocol}//${baseUrl.host}${imgUrl}`;
          }
          
          // Add unique URLs only
          if (imgUrl.startsWith('http') && !extractedImages.includes(imgUrl)) {
            extractedImages.push(imgUrl);
          }
        });
        
        console.log(`✅ Extracted ${imgMatches.length} <img> tags from HTML`);
      }
      
      // Method 2: Also add OG image if exists
      if (result.metadata?.ogImage && !extractedImages.includes(result.metadata.ogImage)) {
        extractedImages.unshift(result.metadata.ogImage); // Add to beginning
      }
      
      console.log(`🖼️ Total REAL images: ${extractedImages.length}`);
      
      return {
        markdown: result.markdown || '',
        html: result.html || '',
        title: result.metadata?.title || '',
        description: result.metadata?.description || '',
        language: result.metadata?.language || 'en',
        images: extractedImages,
        links: result.links || [],
        metadata: result.metadata,
        screenshot: result.screenshot
      };
      
    } catch (error: any) {
      console.error('❌ Firecrawl error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error details:', error.details);
      
      // Parse Firebase Function error codes
      const errorCode = error.code || '';
      const message = error.message || '';
      
      // Authentication error - user not logged in
      if (errorCode === 'functions/unauthenticated' || message.includes('Must be authenticated')) {
        throw new Error('Sie müssen eingeloggt sein, um Websites zu analysieren. Bitte melden Sie sich an.');
      }
      
      // Function not found - not deployed
      if (errorCode === 'functions/not-found' || message.includes('NOT_FOUND')) {
        throw new Error('Die Scraping-Funktion ist nicht verfügbar. Bitte kontaktieren Sie den Support.');
      }
      
      // Internal server error
      if (errorCode === 'functions/internal') {
        // Check if it's a Firecrawl API timeout
        if (message.includes('408') || message.includes('timeout') || message.includes('500')) {
          console.warn('⚠️ Firecrawl API timeout/500 - attempting fallback to direct scraping or partial results');
          throw new Error('Die Website konnte nicht analysiert werden (Timeout). Bitte versuchen Sie es erneut.');
        }
        
        throw new Error(`Firecrawl Scraping fehlgeschlagen: ${message}`);
      }
      
      // Network error
      if (message.includes('network') || message.includes('fetch') || errorCode === 'functions/unavailable') {
        throw new Error('Netzwerkfehler. Bitte prüfen Sie Ihre Internetverbindung.');
      }
      
      // Permission denied
      if (errorCode === 'functions/permission-denied') {
        throw new Error('Keine Berechtigung für diese Aktion.');
      }
      
      throw new Error(`Firecrawl scraping failed: ${message || 'Unbekannter Fehler'}`);
    }
  },

  /**
   * Extract REAL images from website
   * Returns actual image URLs found on the page
   */
  async extractRealImages(url: string): Promise<string[]> {
    try {
      const scraped = await this.scrapeWebsite(url);
      
      // Filter for valid, absolute image URLs
      const validImages = scraped.images.filter(img => {
        try {
          // Must be absolute URL or valid relative
          if (img.startsWith('http://') || img.startsWith('https://')) {
            return true;
          }
          if (img.startsWith('/')) {
            // Make absolute
            const baseUrl = new URL(url);
            return true;
          }
          return false;
        } catch {
          return false;
        }
      }).map(img => {
        // Convert relative to absolute
        if (img.startsWith('/') && !img.startsWith('//')) {
          const baseUrl = new URL(url);
          return `${baseUrl.protocol}//${baseUrl.host}${img}`;
        }
        if (img.startsWith('//')) {
          return `https:${img}`;
        }
        return img;
      });
      
      console.log(`✅ Firecrawl found ${validImages.length} REAL image URLs`);
      
      return validImages;
      
    } catch (error) {
      console.error('Error extracting images:', error);
      return [];
    }
  },

  /**
   * Find REAL conversion forms on website
   * Parses actual HTML to find forms, buttons, conversion points
   */
  async findRealConversions(url: string): Promise<RealConversion[]> {
    try {
      const scraped = await this.scrapeWebsite(url);
      
      console.log('🔍 Analyzing REAL HTML for conversion forms...');
      
      const conversions: RealConversion[] = [];
      
      // Parse HTML for actual <form> elements
      const formRegex = /<form[^>]*>([\s\S]*?)<\/form>/gi;
      const forms = scraped.html.match(formRegex) || [];
      
      forms.forEach((formHtml, index) => {
        const hasEmail = /type\s*=\s*["']email["']/i.test(formHtml);
        const hasPhone = /type\s*=\s*["']tel["']/i.test(formHtml);
        const hasName = /name\s*=\s*["']name["']|id\s*=\s*["']name["']/i.test(formHtml);
        const hasMessage = /<textarea/i.test(formHtml);
        
        // Determine form type based on ACTUAL content
        let type: RealConversion['type'] = 'contact';
        if (formHtml.includes('checkout') || formHtml.includes('cart') || formHtml.includes('kaufen')) {
          type = 'purchase';
        } else if (formHtml.includes('sign') || formHtml.includes('register') || formHtml.includes('anmeldung')) {
          type = 'signup';
        } else if (formHtml.includes('appointment') || formHtml.includes('termin') || formHtml.includes('probefahrt')) {
          type = 'booking';
        } else if (formHtml.includes('download') || formHtml.includes('katalog')) {
          type = 'download';
        }
        
        // Extract button text
        const buttonMatch = formHtml.match(/<button[^>]*>(.*?)<\/button>/i) || 
                           formHtml.match(/value\s*=\s*["'](.*?)["'][^>]*type\s*=\s*["']submit["']/i);
        const buttonText = buttonMatch ? buttonMatch[1].replace(/<[^>]+>/g, '').trim() : '';
        
        conversions.push({
          type,
          hasEmail,
          hasPhone,
          hasName,
          hasMessage,
          buttonText,
          evidence: `Found real <form> element ${index + 1} with ${[
            hasEmail && 'email',
            hasPhone && 'phone',
            hasName && 'name',
            hasMessage && 'message'
          ].filter(Boolean).join(', ')} fields`
        });
      });
      
      console.log(`✅ Found ${conversions.length} REAL conversion forms (no guessing!)`);
      
      return conversions;
      
    } catch (error) {
      console.error('Error finding conversions:', error);
      return [];
    }
  },

  /**
   * Extract branding info using Firecrawl v2.7.0 specialized extract endpoint
   */
  async extractBranding(url: string): Promise<{ 
    logoUrl?: string; 
    primaryColor?: string; 
    secondaryColor?: string; 
    brandName?: string; 
  } | null> {
    try {
      console.log('✨ Calling Firecrawl v2.7.0 Branding Extract...');

      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Not authenticated');
      
      const idToken = await currentUser.getIdToken();
      const functionUrl = 'https://us-central1-aideline-pro.cloudfunctions.net/firecrawlExtractBranding';
      
      const response = await this.fetchWithRetry(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ data: { url } })
      });
      
      if (!response.ok) {
        console.warn('Branding extract failed, falling back to manual detection');
        return null;
      }
      
      const responseData = await response.json();
      const data = responseData.result?.data || responseData.data;
      
      return {
        logoUrl: data.logo_url,
        primaryColor: data.primary_color,
        secondaryColor: data.secondary_color,
        brandName: data.brand_name
      };
      
    } catch (error) {
      console.error('Branding extract error:', error);
      return null;
    }
  },

  /**
   * Extract logo URL from REAL scraped images
   */
  async extractRealLogo(url: string): Promise<string | null> {
    try {
      const scraped = await this.scrapeWebsite(url);
      
      // Look for logo in REAL images
      const logoImage = scraped.images.find(img => 
        img.toLowerCase().includes('logo') ||
        img.toLowerCase().includes('brand') ||
        img.toLowerCase().includes('mark')
      );
      
      if (logoImage) {
        // Make absolute URL
        if (logoImage.startsWith('/')) {
          const baseUrl = new URL(url);
          return `${baseUrl.protocol}//${baseUrl.host}${logoImage}`;
        }
        return logoImage;
      }
      
      // Fallback: Clearbit (also REAL, not hallucinated)
      const domain = new URL(url).hostname.replace('www.', '');
      return `https://logo.clearbit.com/${domain}`;
      
    } catch (error) {
      console.error('Error extracting logo:', error);
      
      // Fallback: Clearbit API (REAL, not fake)
      try {
        const domain = new URL(url).hostname.replace('www.', '');
        return `https://logo.clearbit.com/${domain}`;
      } catch {
        return null;
      }
    }
  },

  /**
   * Extract logo candidates with intelligent scoring
   * Returns multiple candidates for user selection
   * Uses API-first approach (Clearbit + Google) with HTML scraping as fallback
   */
  async extractLogoCandidates(url: string): Promise<Array<{
    url: string;
    score: number;
    format: 'svg' | 'png' | 'jpg' | 'webp' | 'unknown';
    location: 'header' | 'nav' | 'footer' | 'body' | 'api';
    altText?: string;
    linksToHome: boolean;
    source?: 'clearbit' | 'google' | 'scraped';
    estimatedSize?: { width: number; height: number };
  }>>   {
    const candidates: Array<{
      url: string;
      score: number;
      format: 'svg' | 'png' | 'jpg' | 'webp' | 'unknown';
      location: 'header' | 'nav' | 'footer' | 'body' | 'api';
      altText?: string;
      linksToHome: boolean;
      source?: 'clearbit' | 'google' | 'scraped';
      estimatedSize?: { width: number; height: number };
    }> = [];

    try {
      const baseUrl = new URL(url);
      const domain = baseUrl.hostname.replace('www.', '');
      
      // METHOD 1: Clearbit Logo API (highest quality - same as companyLogoService)
      console.log('🎯 Trying Clearbit Logo API...');
      const clearbitUrl = `https://logo.clearbit.com/${domain}`;
      try {
        const response = await fetch(clearbitUrl, { method: 'HEAD' });
        if (response.ok) {
          candidates.push({
            url: clearbitUrl,
            score: 100, // Highest score - most reliable
            format: 'png',
            location: 'api',
            altText: 'Clearbit Logo',
            linksToHome: true,
            source: 'clearbit'
          });
          console.log('✅ Clearbit logo found');
        } else {
          console.log('   Clearbit logo not available (HTTP ' + response.status + ')');
        }
      } catch (error) {
        console.log('   Clearbit logo not available');
      }
      
      // METHOD 2: Direct Favicon from website
      console.log('🎯 Trying direct favicon...');
      const directFaviconUrl = `${baseUrl.protocol}//${baseUrl.hostname}/favicon.ico`;
      try {
        const faviconResponse = await fetch(directFaviconUrl, { method: 'HEAD' });
        if (faviconResponse.ok) {
          candidates.push({
            url: directFaviconUrl,
            score: 95, // High score - direct from source
            format: 'unknown',
            location: 'api',
            altText: 'Direct Favicon',
            linksToHome: true,
            source: 'scraped'
          });
          console.log('✅ Direct favicon found');
        }
      } catch (error) {
        console.log('   Direct favicon not available');
      }
      
      // METHOD 3: Google Favicon API (reliable fallback - same as companyLogoService)
      console.log('🎯 Adding Google Favicon API...');
      const googleFaviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
      candidates.push({
        url: googleFaviconUrl,
        score: 90, // Very high score - always works
        format: 'png',
        location: 'api',
        altText: 'Google Favicon',
        linksToHome: true,
        source: 'google'
      });
      console.log('✅ Google Favicon added');
      
      // METHOD 4: Scraped images from website (lower priority fallback)
      console.log('🎯 Extracting logos from scraped HTML...');
      const scrapedLogos = await this.extractLogosFromHTML(url);
      console.log(`✅ Found ${scrapedLogos.length} scraped logo candidates`);
      
      // Add scraped logos with lower priority (reduced score)
      candidates.push(...scrapedLogos.map(logo => ({
        ...logo,
        score: Math.max(1, logo.score - 10), // Lower priority than API logos
        source: 'scraped' as const
      })));
      
      // Sort by score (highest first) and return top 5
      const topCandidates = candidates
        .sort((a, b) => b.score - a.score)
        .slice(0, 5);
      
      console.log(`✅ Returning ${topCandidates.length} logo candidates (API + scraped)`);
      return topCandidates;
      
    } catch (error) {
      console.error('Error extracting logo candidates:', error);
      
      // Fallback: Return Google Favicon (always works)
      try {
        const domain = new URL(url).hostname.replace('www.', '');
        return [{
          url: `https://www.google.com/s2/favicons?domain=${domain}&sz=128`,
          score: 90,
          format: 'png',
          location: 'api',
          altText: 'Google Favicon',
          linksToHome: true,
          source: 'google'
        }];
      } catch {
        return [];
      }
    }
  },

  /**
   * Extract logos from HTML (private helper method)
   * Only used as fallback when API methods don't find logos
   */
  async extractLogosFromHTML(url: string): Promise<Array<{
    url: string;
    score: number;
    format: 'svg' | 'png' | 'jpg' | 'webp' | 'unknown';
    location: 'header' | 'nav' | 'footer' | 'body';
    altText?: string;
    linksToHome: boolean;
  }>> {
    try {
      const scraped = await this.scrapeWebsite(url);
      const baseUrl = new URL(url);
      
      const candidates = scraped.images.map(imgUrl => {
        const imgLower = imgUrl.toLowerCase();
        let score = 0;
        
        // Determine format
        let format: 'svg' | 'png' | 'jpg' | 'webp' | 'unknown' = 'unknown';
        if (imgLower.endsWith('.svg') || imgLower.includes('.svg?')) {
          format = 'svg';
          score += 40; // SVG is strongly preferred for logos
        } else if (imgLower.endsWith('.png') || imgLower.includes('.png?')) {
          format = 'png';
          score += 30;
        } else if (imgLower.endsWith('.jpg') || imgLower.endsWith('.jpeg') || imgLower.includes('.jpg?') || imgLower.includes('.jpeg?')) {
          format = 'jpg';
          score += 10;
        } else if (imgLower.endsWith('.webp') || imgLower.includes('.webp?')) {
          format = 'webp';
          score += 25;
        }
        
        // Check for logo-indicating keywords in filename/path
        if (imgLower.includes('logo')) score += 50;
        if (imgLower.includes('brand')) score += 40;
        if (imgLower.includes('mark')) score += 30;
        if (imgLower.includes('header')) score += 20;
        if (imgLower.includes('nav')) score += 15;
        
        // Exclude patterns (customer/partner logos, social media)
        if (imgLower.includes('customer') || imgLower.includes('client') || imgLower.includes('partner') || imgLower.includes('reference')) score -= 60;
        if (imgLower.includes('testimonial') || imgLower.includes('review')) score -= 50;
        if (imgLower.includes('facebook') || imgLower.includes('linkedin') || imgLower.includes('twitter') || imgLower.includes('instagram')) score -= 80;
        if (imgLower.includes('icon') && !imgLower.includes('logo')) score -= 20;
        if (imgLower.includes('banner') || imgLower.includes('hero') && !imgLower.includes('logo')) score -= 15;
        
        // Estimate location (simple heuristics from path)
        let location: 'header' | 'nav' | 'footer' | 'body' = 'body';
        if (imgLower.includes('header') || imgLower.includes('top')) location = 'header';
        else if (imgLower.includes('nav') || imgLower.includes('menu')) location = 'nav';
        else if (imgLower.includes('footer') || imgLower.includes('bottom')) location = 'footer';
        
        // Location-based scoring
        if (location === 'header') score += 35;
        else if (location === 'nav') score += 30;
        else if (location === 'footer') score += 10;
        
        // Check if it's likely to link to homepage (basic heuristic)
        const linksToHome = imgLower.includes('logo') || location === 'header' || location === 'nav';
        if (linksToHome) score += 20;
        
        // Make absolute URL
        let absoluteUrl = imgUrl;
        if (imgUrl.startsWith('/')) {
          absoluteUrl = `${baseUrl.protocol}//${baseUrl.host}${imgUrl}`;
        } else if (imgUrl.startsWith('./')) {
          absoluteUrl = `${baseUrl.protocol}//${baseUrl.host}${baseUrl.pathname}${imgUrl.slice(1)}`;
        }
        
        return {
          url: absoluteUrl,
          score: Math.max(0, score), // Don't allow negative scores
          format,
          location,
          linksToHome,
          altText: undefined
        };
      });
      
      // Filter and sort
      return candidates
        .filter(c => c.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 3); // Only return top 3 scraped logos
      
    } catch (error) {
      console.error('Error extracting logos from HTML:', error);
      return [];
    }
  },

  /**
   * Detect German tonality (Du vs. Sie) from REAL content
   * Direct regex parsing - no AI needed
   */
  detectTonality(markdown: string, html: string): TonalityAnalysis {
    console.log('🎯 Detecting Du/Sie tonality from REAL content...');
    
    // Count Sie-Form indicators (formal)
    const sieRegex = /\b(Sie|Ihnen|Ihr|Ihre|Ihrem|Ihrer)\b/g;
    const sieMatches = markdown.match(sieRegex) || [];
    const sieCount = sieMatches.length;
    
    // Count Du-Form indicators (informal)
    const duRegex = /\b(Du|Dir|Dein|Deine|Deinem|Deiner|Dich)\b/g;
    const duMatches = markdown.match(duRegex) || [];
    const duCount = duMatches.length;
    
    // Extract example sentences
    const sieExample = markdown.match(/[^.!?]*\b(Sie|Ihnen)\b[^.!?]*[.!?]/)?.[0]?.trim();
    const duExample = markdown.match(/[^.!?]*\b(Du|Dir)\b[^.!?]*[.!?]/)?.[0]?.trim();
    
    let addressForm: 'Sie' | 'Du' | 'mixed' | 'none';
    let formality: 'formal' | 'informal';
    let evidence: string;
    let recommendation: string;
    
    const total = sieCount + duCount;
    
    if (total === 0) {
      // No German address form found (likely English content)
      addressForm = 'none';
      formality = 'formal';
      evidence = 'No German address forms detected (likely English content or no personal address)';
      recommendation = 'Use neutral professional language in ads';
    } else if (sieCount > duCount * 2) {
      // Clearly Sie-Form dominant
      addressForm = 'Sie';
      formality = 'formal';
      evidence = sieExample || `Found ${sieCount} Sie-Form occurrences vs. ${duCount} Du-Form`;
      recommendation = 'Use formal Sie-Form in ad copy (e.g., "Kontaktieren Sie uns", "Profitieren Sie von...")';
    } else if (duCount > sieCount * 2) {
      // Clearly Du-Form dominant
      addressForm = 'Du';
      formality = 'informal';
      evidence = duExample || `Found ${duCount} Du-Form occurrences vs. ${sieCount} Sie-Form`;
      recommendation = 'Use informal Du-Form in ad copy (e.g., "Kontaktiere uns", "Profitiere von...")';
    } else {
      // Mixed or unclear
      addressForm = 'mixed';
      formality = 'formal'; // Default to formal when mixed
      evidence = `Mixed usage: ${sieCount} Sie-Form, ${duCount} Du-Form occurrences`;
      recommendation = 'Use formal Sie-Form in ads (safer choice for mixed content)';
    }
    
    // Calculate confidence
    const dominantCount = Math.max(sieCount, duCount);
    const confidence = total > 0 
      ? Math.min(95, Math.round((dominantCount / total) * 100))
      : 0;
    
    console.log(`✅ Tonality detected: ${addressForm} (${confidence}% confidence)`);
    console.log(`   Sie: ${sieCount}, Du: ${duCount}`);
    
    return {
      formality,
      addressForm,
      confidence,
      evidence,
      recommendation,
      sieCount,
      duCount
    };
  },

  /**
   * Detect all languages on website from REAL HTML
   * Direct parsing - no AI needed
   */
  extractAllLanguages(html: string, markdown: string): string[] {
    console.log('🌐 Extracting all languages from REAL HTML...');
    
    const detectedLanguages = new Set<string>();
    
    // 1. Parse lang attributes from HTML tags
    const langAttrRegex = /lang\s*=\s*["']([a-z]{2}(-[A-Z]{2})?)['"]/gi;
    const langMatches = [...html.matchAll(langAttrRegex)];
    langMatches.forEach(match => {
      const langCode = match[1].split('-')[0]; // Get just 'de' from 'de-DE'
      detectedLanguages.add(langCode);
    });
    
    // 2. Look for language switcher links (common pattern: /de/, /en/, /fr/)
    const switcherRegex = /href\s*=\s*["'][^"']*\/(de|en|fr|it|es|nl|pt|pl|cs|ru|zh|ja)(?:\/|["'])/gi;
    const switcherMatches = [...html.matchAll(switcherRegex)];
    switcherMatches.forEach(match => {
      detectedLanguages.add(match[1]);
    });
    
    // 3. Look for hreflang attributes (SEO multi-language indicator)
    const hreflangRegex = /hreflang\s*=\s*["']([a-z]{2})(-[A-Z]{2})?["']/gi;
    const hreflangMatches = [...html.matchAll(hreflangRegex)];
    hreflangMatches.forEach(match => {
      const langCode = match[1];
      detectedLanguages.add(langCode);
    });
    
    const languages = Array.from(detectedLanguages);
    console.log(`✅ Detected ${languages.length} language(s): ${languages.join(', ')}`);
    
    return languages;
  },

  /**
   * Extract SEO metadata from REAL HTML
   * Direct parsing - no AI needed
   */
  extractSEOMetadata(html: string, metadata: any): {
    title: string;
    description: string;
    keywords: string[];
    headings: {
      h1: string[];
      h2: string[];
      h3: string[];
    };
    canonicalUrl?: string;
    ogTitle?: string;
    ogDescription?: string;
    ogImage?: string;
    ogType?: string;
    structuredData: any[];
    robots?: string;
  } {
    console.log('📊 Extracting SEO metadata from REAL HTML...');
    
    // 1. Extract meta keywords
    const keywordsMatch = html.match(/<meta\s+name=["']keywords["']\s+content=["']([^"']+)["']/i);
    const keywords = keywordsMatch 
      ? keywordsMatch[1].split(',').map(k => k.trim()).filter(k => k.length > 0)
      : [];
    
    // 2. Extract all headings
    const h1Matches = [...html.matchAll(/<h1[^>]*>(.*?)<\/h1>/gi)];
    const h2Matches = [...html.matchAll(/<h2[^>]*>(.*?)<\/h2>/gi)];
    const h3Matches = [...html.matchAll(/<h3[^>]*>(.*?)<\/h3>/gi)];
    
    const headings = {
      h1: h1Matches.map(m => m[1].replace(/<[^>]+>/g, '').trim()).filter(h => h.length > 0),
      h2: h2Matches.map(m => m[1].replace(/<[^>]+>/g, '').trim()).filter(h => h.length > 0),
      h3: h3Matches.map(m => m[1].replace(/<[^>]+>/g, '').trim()).filter(h => h.length > 0)
    };
    
    // 3. Extract canonical URL
    const canonicalMatch = html.match(/<link\s+rel=["']canonical["']\s+href=["']([^"']+)["']/i);
    const canonicalUrl = canonicalMatch ? canonicalMatch[1] : undefined;
    
    // 4. Extract JSON-LD structured data
    const jsonLdMatches = [...html.matchAll(/<script\s+type=["']application\/ld\+json["'][^>]*>(.*?)<\/script>/gis)];
    const structuredData = jsonLdMatches.map(m => {
      try {
        return JSON.parse(m[1].trim());
      } catch {
        return null;
      }
    }).filter(Boolean);
    
    // 5. Extract robots meta
    const robotsMatch = html.match(/<meta\s+name=["']robots["']\s+content=["']([^"']+)["']/i);
    
    // 6. Extract Open Graph data (from metadata or HTML)
    const ogTitleMatch = html.match(/<meta\s+property=["']og:title["']\s+content=["']([^"']+)["']/i);
    const ogDescMatch = html.match(/<meta\s+property=["']og:description["']\s+content=["']([^"']+)["']/i);
    const ogImageMatch = html.match(/<meta\s+property=["']og:image["']\s+content=["']([^"']+)["']/i);
    const ogTypeMatch = html.match(/<meta\s+property=["']og:type["']\s+content=["']([^"']+)["']/i);
    
    console.log(`✅ SEO extracted: ${headings.h1.length} H1s, ${headings.h2.length} H2s, ${keywords.length} keywords, ${structuredData.length} JSON-LD schemas`);
    
    return {
      title: metadata?.title || '',
      description: metadata?.description || '',
      keywords,
      headings,
      canonicalUrl,
      ogTitle: ogTitleMatch ? ogTitleMatch[1] : metadata?.ogTitle,
      ogDescription: ogDescMatch ? ogDescMatch[1] : metadata?.ogDescription,
      ogImage: ogImageMatch ? ogImageMatch[1] : metadata?.ogImage,
      ogType: ogTypeMatch ? ogTypeMatch[1] : metadata?.ogType,
      structuredData,
      robots: robotsMatch ? robotsMatch[1] : undefined
    };
  },

  /**
   * Check if Firecrawl is configured
   * 
   * NOTE: We use Firebase Cloud Function (firecrawlProxyV2) for scraping,
   * which has its own API key configured server-side.
   * The frontend API key (VITE_FIRECRAWL_API_KEY) is NOT used!
   * 
   * Always return true since the Cloud Function handles the API key.
   */
  isConfigured(): boolean {
    // Cloud Function handles API key - always available!
    return true;
  },

  /**
   * Helper to fetch with retry logic for 429 Rate Limits
   */
  async fetchWithRetry(
    url: string,
    options: RequestInit,
    retries = 3,
    delay = 2000,
    timeoutMs = 45000
  ): Promise<Response> {
    const retryableStatusCodes = new Set([408, 425, 429, 500, 502, 503, 504]);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);

      if (retryableStatusCodes.has(response.status) && retries > 0) {
        console.warn(
          `⚠️ Firecrawl request returned ${response.status}. Retrying in ${delay / 1000}s... (${retries} retries left)`
        );
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.fetchWithRetry(url, options, retries - 1, delay * 2, timeoutMs);
      }

      return response;
    } catch (error: any) {
      const isAbort = error?.name === 'AbortError';
      if (retries > 0) {
        console.warn(
          `⚠️ ${isAbort ? 'Request timeout' : 'Network error'}. Retrying in ${delay / 1000}s... (${retries} retries left)`,
          error
        );
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.fetchWithRetry(url, options, retries - 1, delay * 2, timeoutMs);
      }
      throw error;
    }
  }
};

